/**
 * Private Classes PDP — see docs/private-classes-pdp.md
 *
 * Owns the whole page: location filtering, the session modal, selection state and
 * add-to-cart. `signature-product-section.js` is deliberately NOT loaded here — its
 * document-level click listener double-fires add-to-cart, and its accordion writes a
 * fixed max-height from scrollHeight that cannot be re-measured after filtering.
 *
 * Location filtering costs one attribute write. Everything for both formats is already
 * in the DOM, so switching never touches the network.
 */
if (!customElements.get('private-classes-pdp')) {
  customElements.define(
    'private-classes-pdp',
    class PrivateClassesPdp extends HTMLElement {
      connectedCallback() {
        this.bookedSlots = [];
        this.scrollY = 0;

        // Every customer-facing string this file writes comes from section settings, and the
        // money format is the shop's own. Read once here rather than per update, and left on
        // the host element so refreshSection() — which only replaces innerHTML — keeps them.
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.countOne = this.dataset.countOne || '1 SESSION SELECTED';
        this.countOther = this.dataset.countOther || '[count] SESSIONS SELECTED';
        this.classesSelected = this.dataset.classesSelected || 'CLASSES SELECTED';
        this.errorInCart = this.dataset.errorInCart || '';
        this.errorTaken = this.dataset.errorTaken || '';
        this.errorGeneric = this.dataset.errorGeneric || '';

        this.onFormatChange = this.onFormatChange.bind(this);
        this.onSlotChange = this.onSlotChange.bind(this);
        this.onDayToggle = this.onDayToggle.bind(this);
        this.onLocationAccordion = this.onLocationAccordion.bind(this);
        this.onOpenModal = this.onOpenModal.bind(this);
        this.onCloseModal = this.onCloseModal.bind(this);
        this.onBook = this.onBook.bind(this);
        this.onAddToCart = this.onAddToCart.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.bindUI();

        // Liquid already greyed out what was in the cart at render time, so nothing is fetched
        // here. This only catches the cart changing afterwards — a line removed in the drawer
        // has to hand its session back. cart.js publishes this on every quantity change.
        // Subscribed once, outside bindUI, so a section swap does not stack subscriptions.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            // This section publishes the same event after its own add, and it has already
            // applied that change locally — re-reading the cart for it would be a wasted
            // round trip. Same guard as quick-order-list.js:33.
            if (event && event.source === PrivateClassesPdp.SOURCE) return;
            this.refreshCartState();
          });
        }
      }

      disconnectedCallback() {
        this.unbindUI();

        // Only added while the modal is open, but make sure a mid-flight teardown cleans up.
        document.removeEventListener('keydown', this.onKeydown);
        this.unlockScroll();

        if (this.cartUnsubscribe) this.cartUnsubscribe();
      }

      /* ----------------------------------------------------------------- binding */

      /**
       * Collects the elements and wires the listeners. Split out of connectedCallback because
       * refreshSection() replaces the markup wholesale — every reference it captured dies with
       * the old DOM, so the pair has to be re-runnable.
       */
      bindUI() {
        this.formatInputs = Array.from(this.querySelectorAll('[data-private-classes-format]'));
        this.slotInputs = Array.from(this.querySelectorAll('[data-private-classes-slot]'));
        this.dayToggles = Array.from(this.querySelectorAll('[data-private-classes-day-toggle]'));
        this.closeButtons = Array.from(this.querySelectorAll('[data-private-classes-modal-close]'));

        this.locationAccordion = this.querySelector('[data-private-classes-accordion-trigger]');
        this.overlay = this.querySelector('[data-private-classes-modal-overlay]');
        this.dialog = this.querySelector('[data-private-classes-modal-popup]');
        this.chooseDatesBtn = this.querySelector('[data-private-classes-choose-dates]');
        this.bookBtn = this.querySelector('[data-private-classes-book]');
        this.addToCartBtn = this.querySelector('[data-private-classes-add-to-cart]');
        this.errorEl = this.querySelector('[data-private-classes-error]');
        this.pointsNoteEl = this.querySelector('[data-private-classes-points-note]');

        this.panelTotalEl = this.querySelector('[data-private-classes-total]');
        this.modalTotalEl = this.querySelector('[data-private-classes-modal-total]');
        this.modalCountEl = this.querySelector('[data-private-classes-modal-count]');

        // Liquid owns the default label, so read it rather than hardcoding it.
        this.chooseDatesLabel = this.chooseDatesBtn ? this.chooseDatesBtn.textContent.trim() : 'CHOOSE DATES';

        this.formatInputs.forEach((input) => input.addEventListener('change', this.onFormatChange));
        this.slotInputs.forEach((input) => input.addEventListener('change', this.onSlotChange));
        this.dayToggles.forEach((toggle) => toggle.addEventListener('click', this.onDayToggle));
        this.closeButtons.forEach((button) => button.addEventListener('click', this.onCloseModal));

        if (this.locationAccordion) this.locationAccordion.addEventListener('click', this.onLocationAccordion);
        if (this.chooseDatesBtn) this.chooseDatesBtn.addEventListener('click', this.onOpenModal);
        if (this.bookBtn) this.bookBtn.addEventListener('click', this.onBook);
        if (this.addToCartBtn) this.addToCartBtn.addEventListener('click', this.onAddToCart);
      }

      unbindUI() {
        if (!this.formatInputs) return;

        this.formatInputs.forEach((input) => input.removeEventListener('change', this.onFormatChange));
        this.slotInputs.forEach((input) => input.removeEventListener('change', this.onSlotChange));
        this.dayToggles.forEach((toggle) => toggle.removeEventListener('click', this.onDayToggle));
        this.closeButtons.forEach((button) => button.removeEventListener('click', this.onCloseModal));

        if (this.locationAccordion) this.locationAccordion.removeEventListener('click', this.onLocationAccordion);
        if (this.chooseDatesBtn) this.chooseDatesBtn.removeEventListener('click', this.onOpenModal);
        if (this.bookBtn) this.bookBtn.removeEventListener('click', this.onBook);
        if (this.addToCartBtn) this.addToCartBtn.removeEventListener('click', this.onAddToCart);
      }

      /* ---------------------------------------------------------- section refresh */

      /**
       * Re-renders the section from the server. This is the only way to learn that another
       * customer took a session: availability is decided in Liquid, so nothing short of asking
       * Liquid again can update the slot list, the location radios, WEEKLY DAYS, TIME SLOT or
       * the SOLD OUT state. It runs on add-to-cart failure only — deliberately not on page load
       * or on opening the modal, which would put a request back on the fast paths.
       *
       * @returns {Promise<boolean>} whether the markup was actually replaced
       */
      refreshSection() {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) return Promise.resolve(false);

        const url = `${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`;

        return fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const fresh = new DOMParser().parseFromString(text, 'text/html').querySelector('private-classes-pdp');
            if (!fresh) return false;

            const chosenFormat = this.dataset.activeFormat;
            const wasBooked = this.bookedSlots.slice();

            this.unbindUI();
            this.innerHTML = fresh.innerHTML;
            this.dataset.activeFormat = fresh.dataset.activeFormat;
            this.bindUI();

            // Keep the customer on the location they picked, unless it just sold out.
            const keepsFormat = this.formatInputs.some(
              (input) => input.value === chosenFormat && input.disabled === false,
            );
            if (keepsFormat) {
              this.dataset.activeFormat = chosenFormat;
              this.formatInputs.forEach((input) => {
                input.checked = input.value === chosenFormat;
              });
            }

            // A booking only survives if its session came back still bookable.
            const live = new Map(
              this.slotInputs.filter((input) => input.disabled === false).map((input) => [input.value, input]),
            );
            this.bookedSlots = wasBooked.filter((slot) => live.has(String(slot.id)));
            this.bookedSlots.forEach((slot) => {
              live.get(String(slot.id)).checked = true;
            });

            this.applyBooking();
            this.syncSelection();
            return true;
          })
          .catch(() => false);
      }

      /* -------------------------------------------------------------- cart state */

      /**
       * A session already in the basket is not sold out — its seat is simply spoken for by this
       * customer. It stays visible but greyed and locked, because removing it from the list made
       * a session the customer had just added look like it had disappeared. Genuinely sold-out
       * seats never reach the DOM, so every input here is one the cart can lock or release.
       *
       * @param {Set<string>} inCartIds every variant id currently in the cart
       */
      applyCartState(inCartIds) {
        this.slotInputs.forEach((input) => {
          const slot = input.closest('.private-classes-modal__slot');
          if (!slot) return;

          const inCart = inCartIds.has(String(input.value));
          slot.classList.toggle('private-classes-modal__slot--in-cart', inCart);
          input.disabled = inCart;
          if (inCart) input.checked = false;
        });

        this.bookedSlots = this.bookedSlots.filter((slot) => !inCartIds.has(String(slot.id)));
        this.applyBooking();
        this.syncSelection();
      }

      /**
       * Re-reads the cart and re-applies the grey-out. Returns the promise, because
       * applyCartState() clears the error banner — a caller that wants to say something
       * afterwards has to wait for this to settle first.
       */
      refreshCartState() {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

        return fetch(`${cartUrl}.js`, { credentials: 'same-origin' })
          .then((response) => response.json())
          .then((cart) => {
            const ids = (cart.items || []).map((item) => String(item.variant_id || item.id));
            this.applyCartState(new Set(ids));
          })
          .catch(() => {
            // A stale grey-out is recoverable — /cart/add.js still rejects a duplicate.
          });
      }

      /* ---------------------------------------------------------------- location */

      /**
       * The entire location filter: one attribute write. CSS hides the non-matching
       * slots, date groups, weekly-days and time-slot rows in a single style pass.
       */
      onFormatChange(event) {
        this.dataset.activeFormat = event.target.value;
        this.dropHiddenSelections();
      }

      /**
       * A session the customer can no longer see must not keep counting towards the total,
       * so drop it from both the modal checkboxes and the already-applied panel booking.
       */
      dropHiddenSelections() {
        const active = this.dataset.activeFormat;
        if (active === 'ALL') return;

        this.slotInputs.forEach((input) => {
          if (input.checked && PrivateClassesPdp.formatOf(input) !== active) input.checked = false;
        });

        const kept = this.bookedSlots.filter((slot) => slot.format === active);
        if (kept.length !== this.bookedSlots.length) {
          this.bookedSlots = kept;
          this.applyBooking();
        }

        this.syncSelection();
      }

      onLocationAccordion() {
        const trigger = this.locationAccordion;
        const content = this.querySelector('[data-private-classes-accordion-content]');
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';

        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        if (content) {
          content.style.maxHeight = isOpen ? '0' : `${content.scrollHeight}px`;
          content.classList.toggle('is-open', !isOpen);
        }
      }

      /* ------------------------------------------------------------------- modal */

      /**
       * Same open/close contract as the Conversation Lab date picker: `is-active` on the
       * overlay and on the popup, which CSS fades and scales in.
       */
      onOpenModal() {
        if (!this.dialog) return;

        this.lastFocused = document.activeElement;
        if (this.overlay) this.overlay.classList.add('is-active');
        this.dialog.classList.add('is-active');
        this.lockScroll();
        document.addEventListener('keydown', this.onKeydown);

        // Calling focus() in the same tick as the class toggle is a no-op: the dialog is
        // still `visibility: hidden` until the browser flushes the style recalc, and a
        // hidden element cannot take focus. A SINGLE rAF is not enough either — the class
        // toggle's style recalc is still pending inside that first callback (measured on
        // this exact popup: at rAF #1, getComputedStyle(dialog).visibility still reads
        // "hidden"; the browser only settles it to "visible" by rAF #2). focus() called at
        // rAF #1 silently fails and falls back to <body> with no error. Waiting for the
        // second rAF is the standard fix for "focus an element right after making it
        // visible" and is verified stable here (checked 400ms out, focus held).
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.dialog.focus());
        });
      }

      onCloseModal() {
        if (!this.dialog) return;

        // Closing without booking must not leave the panel showing a selection the modal
        // no longer has. Booking sets bookedSlots first, so this is a no-op on that path.
        this.restoreSelection();

        if (this.overlay) this.overlay.classList.remove('is-active');
        this.dialog.classList.remove('is-active');
        this.unlockScroll();
        document.removeEventListener('keydown', this.onKeydown);

        if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
          this.lastFocused.focus();
        }
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          this.onCloseModal();
          return;
        }
        if (event.key !== 'Tab') return;

        // Recomputed on every Tab, so filtering or collapsing a group can never leave
        // the trap pointing at an element that is no longer reachable.
        const focusables = this.getReachableElements();
        if (focusables.length === 0) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      getReachableElements() {
        if (!this.dialog) return [];

        const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        return Array.from(this.dialog.querySelectorAll(selector)).filter((el) => {
          if (el.disabled || el.offsetParent === null) return false;

          // A collapsed group still exposes its own head button — only its slots go away.
          if (el.hasAttribute('data-private-classes-day-toggle')) return true;

          const day = el.closest('.private-classes-modal__day');
          return !day || day.classList.contains('is-open');
        });
      }

      lockScroll() {
        this.scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollY}px`;
        document.body.style.width = '100%';
      }

      unlockScroll() {
        if (document.body.style.position !== 'fixed') return;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, this.scrollY);
      }

      /* --------------------------------------------------------------- selection */

      onDayToggle(event) {
        const toggle = event.currentTarget;
        const day = toggle.closest('.private-classes-modal__day');
        if (!day) return;

        const isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        day.classList.toggle('is-open', !isOpen);
      }

      onSlotChange() {
        this.syncSelection();
      }

      get selectedSlots() {
        return this.slotInputs.filter((input) => input.checked);
      }

      /** Recomputes the day indicators and the modal footer from the checked inputs. */
      syncSelection() {
        const selected = this.selectedSlots;
        const total = selected.reduce((sum, input) => sum + (parseInt(input.dataset.price, 10) || 0), 0);

        this.querySelectorAll('[data-private-classes-day]').forEach((day) => {
          const hasChecked = day.querySelector('[data-private-classes-slot]:checked') !== null;
          day.classList.toggle('is-selected', hasChecked);
        });

        this.setMoney(this.modalTotalEl, total);
        if (this.modalCountEl) this.modalCountEl.textContent = this.countLabel(selected.length);
        if (this.bookBtn) this.bookBtn.disabled = selected.length === 0;
      }

      /* -------------------------------------------------------- book & add to cart */

      /** Puts the modal checkboxes back in step with what the panel is currently showing. */
      restoreSelection() {
        const booked = new Set(this.bookedSlots.map((slot) => slot.id));
        this.slotInputs.forEach((input) => {
          input.checked = booked.has(input.value);
        });
        this.syncSelection();
      }

      onBook() {
        this.bookedSlots = this.selectedSlots.map((input) => ({
          id: input.value,
          price: parseInt(input.dataset.price, 10) || 0,
          date: input.dataset.dateLabel,
          time: input.dataset.timeLabel,
          location: input.dataset.locationLabel,
          format: PrivateClassesPdp.formatOf(input),
        }));

        this.applyBooking();
        this.onCloseModal();
      }

      applyBooking() {
        const count = this.bookedSlots.length;
        const total = this.bookedSlots.reduce((sum, slot) => sum + slot.price, 0);
        this.setMoney(this.panelTotalEl, total);

        if (this.chooseDatesBtn) {
          if (count === 0) {
            this.chooseDatesBtn.textContent = this.chooseDatesLabel;
          } else if (count === 1) {
            const slot = this.bookedSlots[0];
            this.chooseDatesBtn.textContent = `${slot.date} | ${slot.time} | ${slot.location}`;
          } else {
            this.chooseDatesBtn.textContent = `${count} ${this.classesSelected}`;
          }
        }

        if (this.addToCartBtn) {
          this.addToCartBtn.disabled = count === 0;
          this.addToCartBtn.setAttribute(
            'data-selected-variants',
            JSON.stringify(this.bookedSlots.map((slot) => ({ id: slot.id, quantity: 1 }))),
          );
        }

        // Only rendered for customers whose points can actually be redeemed, and only
        // shown once there is a booking for them to be redeemed against.
        if (this.pointsNoteEl) this.pointsNoteEl.hidden = count === 0;

        this.clearError();
      }

      onAddToCart() {
        if (this.bookedSlots.length === 0) return;

        this.clearError();
        this.addToCartBtn.classList.add('loading');
        this.addToCartBtn.disabled = true;

        const cartDrawer = document.querySelector('cart-drawer');
        const body = {
          items: this.bookedSlots.map((slot) => ({ id: parseInt(slot.id, 10), quantity: 1 })),
        };

        // Ask for the drawer's sections in the same request, otherwise the drawer opens
        // showing the pre-add cart.
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }

        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

        fetch(`${addUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((json) => {
            // /cart/add.js reports failures in the body, not only via the status code.
            if (json.status) throw new Error(json.description || json.message);

            // The response carries only the lines just added, so union them with what was
            // already greyed out rather than fetching the whole cart back.
            const inCart = new Set(
              this.slotInputs.filter((input) => input.disabled).map((input) => String(input.value)),
            );
            this.bookedSlots.forEach((slot) => inCart.add(String(slot.id)));
            this.applyCartState(inCart);

            // The theme's cart consumers listen on the pub/sub channel — cart.js:31 — not on a
            // DOM event, so this has to go through publish() to be heard at all. The drawer is
            // still rendered directly below: it takes the sections that came back with this
            // very response, which is fresher than anything a listener could re-fetch.
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: PrivateClassesPdp.SOURCE, cartData: json });
            }

            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(json);
            } else {
              window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            }
          })
          .catch((error) => {
            // Shopify's own wording is internal, untranslated platform copy, so it goes to the
            // console and the customer reads one of the section's authored messages instead.
            console.warn('[private-classes] add to cart failed:', (error && error.message) || error);

            const attempted = this.bookedSlots.map((slot) => String(slot.id));

            // Two causes, both needing fresh server state: the session is already in this
            // basket (one seat each, so the second add is refused as "sold out"), or another
            // customer took it while this page sat open. A re-render answers both — it drops
            // what is gone and greys what is held — and is what stops the customer clicking
            // into the identical error for ever. Fall back to a cart read if it cannot run.
            // The message goes last, because both paths end in applyBooking(), which clears it,
            // and because which message is right is only knowable once the refresh has landed.
            this.refreshSection()
              .then((replaced) => (replaced ? null : this.refreshCartState()))
              .then(() => {
                this.showError(this.messageFor(attempted));
                if (this.bookedSlots.length > 0) this.addToCartBtn.disabled = false;
              });
          })
          .finally(() => {
            this.addToCartBtn.classList.remove('loading');
          });
      }

      /**
       * Names the cause from the page as it now stands rather than by matching Shopify's error
       * text, which is English platform copy and can be reworded on their side at any time. The
       * refresh has already run: a session the cart holds came back locked, and one another
       * customer took is no longer in the list at all.
       *
       * @param {string[]} attemptedIds the variant ids the failed add tried to buy
       * @returns {string} the message to show, or '' if the merchant has cleared them
       */
      messageFor(attemptedIds) {
        const byId = new Map(this.slotInputs.map((input) => [String(input.value), input]));
        const stillListed = attemptedIds.filter((id) => byId.has(id));

        if (stillListed.length < attemptedIds.length) return this.errorTaken || this.errorGeneric;
        if (stillListed.some((id) => byId.get(id).disabled)) return this.errorInCart || this.errorGeneric;
        return this.errorGeneric;
      }

      showError(message) {
        if (!this.errorEl || !message) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      /* ------------------------------------------------------------------ helpers */

      /**
       * Renders cents through the shop's own `money_format`, so the total the JS writes matches
       * the one Liquid rendered into the same element on load. Handles the four placeholders
       * Shopify supports; an unrecognised format falls back to its raw amount rather than
       * printing a currency this shop does not use.
       */
      formatMoney(cents) {
        const amount = cents / 100;

        const withDelimiters = (decimals, thousands, decimalMark) => {
          const fixed = Math.abs(amount).toFixed(decimals);
          const parts = fixed.split('.');
          const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          const body = parts[1] ? whole + decimalMark + parts[1] : whole;
          return amount < 0 ? `-${body}` : body;
        };

        return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, placeholder) => {
          switch (placeholder) {
            case 'amount':
              return withDelimiters(2, ',', '.');
            case 'amount_no_decimals':
              return withDelimiters(0, ',', '.');
            case 'amount_with_comma_separator':
              return withDelimiters(2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withDelimiters(0, '.', ',');
            default:
              return withDelimiters(2, ',', '.');
          }
        });
      }

      /**
       * "1 SESSION SELECTED" / "3 SESSIONS SELECTED", both authored in the theme editor so a
       * storefront in another language can say it its own way. Appending an S here would only
       * ever have worked in English.
       */
      /**
       * Writes a money value the way Liquid's own `money` filter does — as HTML. Shop formats
       * are authored with entities (`&euro;{{amount}}`) and some carry markup
       * (`<span class="money">…</span>`); textContent prints either one literally, which is
       * exactly how this element came to show one thing on load and another after a click.
       * Both halves are shop-owned: the format is a store setting and the amount is a number
       * this file produced.
       */
      setMoney(element, cents) {
        if (element) element.innerHTML = this.formatMoney(cents);
      }

      countLabel(count) {
        const template = count === 1 ? this.countOne : this.countOther;
        return template.replace('[count]', count);
      }

      /** Names this section on the cart pub/sub channel, so it can skip its own publish. */
      static get SOURCE() {
        return 'private-classes';
      }

      /** ONLINE / OFFLINE, read off the slot's wrapper. */
      static formatOf(input) {
        const wrapper = input.closest('[data-format]');
        return wrapper ? wrapper.getAttribute('data-format') : '';
      }
    },
  );
}
